import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { formatDate } from "../../../../../../formatDate";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Window from "../../../../../elements/window";

function ListOutlet(props) {
  const location = useLocation();
  const [table, setTable] = useState({
    head: [
      "Status",
      "Created At",
      "Last Update",
      "Group",
      "Brand",
      "Outlet",
      "Actions",
    ],
    data: false,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterGroup, setFilterGroup] = useState("all");
  const [filterBrand, setFilterBrand] = useState("all");
  const [sortBy, setSortBy] = useState("status");
  const [sortOrder, setSortOrder] = useState("desc");
  const [load, setLoad] = useState(0); // 0: loading, 1: data loaded, 2: ready for export

  // Ref to track previous export state
  const prevExport = useRef(0);

  useEffect(() => {
    const fetchData = async () => {
      if (props.user && props.user.data) {
        setLoad(0); // Start loading
        try {
          const response = await axios.get(
            props.chimeraserv.urlGet + "/group",
            {
              headers: {
                Authorization: props.user.data.usrKey,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );

          let data = [];
          if (Object.keys(response.data).length) {
            data = response.data;
          }
          setTable((prev) => ({
            ...prev,
            data: data,
          }));
          setLoad(1); // Data loaded
        } catch (error) {
          setTable((prev) => ({
            ...prev,
            data: [],
          }));
          setLoad(1); // Even on error, mark as loaded
        }
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [props.user, props.chimeraserv.urlGet]);

  // Set load to 2 when data is ready
  useEffect(() => {
    if (load === 1 && table.data !== false) {
      setLoad(2);
    }
  }, [load, table.data]);

  // Create flat outlet data for filtering and searching
  const flatOutletData = useMemo(() => {
    if (!table.data) return [];

    return [].concat.apply(
      [],
      [].concat.apply(
        [],
        table.data.map((group, groupIndex) =>
          group.brands.map((brand, brandIndex) =>
            brand.chains.map((chain) => ({
              ...chain,
              data: group,
              group: {
                index: groupIndex,
                grpKey: group.grpKey,
                _id: group._id,
                name: group.name,
              },
              brand: {
                index: brandIndex,
                braKey: brand.braKey,
                name: brand.name,
              },
            }))
          )
        )
      )
    );
  }, [table.data]);

  // Get unique groups and brands for filter dropdowns
  const availableGroups = useMemo(() => {
    if (!table.data) return [];
    return table.data.map((group) => ({
      id: group._id,
      name: group.name,
    }));
  }, [table.data]);

  const availableBrands = useMemo(() => {
    if (!table.data) return [];
    const brands = [];
    table.data.forEach((group) => {
      group.brands.forEach((brand) => {
        brands.push({
          id: brand.braKey,
          name: brand.name,
          groupId: group._id,
        });
      });
    });
    // Remove duplicates by brand key
    return brands.filter(
      (brand, index, self) => index === self.findIndex((b) => b.id === brand.id)
    );
  }, [table.data]);

  // Filter, search, and sort logic
  const filteredAndSortedData = useMemo(() => {
    if (!flatOutletData.length) return [];

    let filtered = flatOutletData.filter((outlet) => {
      // Search filter
      const matchesSearch =
        searchTerm === "" ||
        outlet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        outlet.group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        outlet.brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        outlet.chaKey.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter (online/offline)
      const matchesStatus =
        filterStatus === "all" ||
        (filterStatus === "online" && outlet.info.status === 1) ||
        (filterStatus === "offline" && outlet.info.status === 0) ||
        (filterStatus === "expired" &&
          new Date(outlet.outletExp ? outlet.outletExp * 1000 : 0) <
            new Date());

      // Group filter
      const matchesGroup =
        filterGroup === "all" || outlet.group._id === filterGroup;

      // Brand filter
      const matchesBrand =
        filterBrand === "all" || outlet.brand.braKey === filterBrand;

      return matchesSearch && matchesStatus && matchesGroup && matchesBrand;
    });

    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "status":
          aValue = a.info.status;
          bValue = b.info.status;
          break;
        case "outlet":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "group":
          aValue = a.group.name.toLowerCase();
          bValue = b.group.name.toLowerCase();
          break;
        case "brand":
          aValue = a.brand.name.toLowerCase();
          bValue = b.brand.name.toLowerCase();
          break;
        case "created":
          aValue = new Date(a.info.firstUpdate * 1000);
          bValue = new Date(b.info.firstUpdate * 1000);
          break;
        case "updated":
          aValue = new Date(a.info.lastUpdate * 1000);
          bValue = new Date(b.info.lastUpdate * 1000);
          break;
        default:
          aValue = a.info.status;
          bValue = b.info.status;
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [
    flatOutletData,
    searchTerm,
    filterStatus,
    filterGroup,
    filterBrand,
    sortBy,
    sortOrder,
  ]);

  // Component that receives props from Window component - handles export
  const OutletListContent = ({ export: exportTime, getData }) => {
    // Export logic
    useEffect(() => {
      if (prevExport.current !== exportTime && load === 2 && getData) {
        prevExport.current = exportTime;

        // Prepare export data based on filtered results
        const exportData = filteredAndSortedData.map((outlet, index) => {
          const status = outlet.info.status === 1 ? "Online" : "Offline";
          const isExpired =
            new Date(outlet.outletExp ? outlet.outletExp * 1000 : 0) <
            new Date();
          const expiredStatus = isExpired ? " (Expired)" : "";

          return [
            (index + 1).toString(),
            status,
            formatDate.longShort(outlet.info.firstUpdate),
            formatDate.longShort(outlet.info.lastUpdate),
            outlet.group.name,
            outlet.brand.name,
            outlet.name + expiredStatus,
            // outlet.chaKey,
          ];
        });

        // Get summary statistics
        const totalOutlets = filteredAndSortedData.length;
        const onlineOutlets = filteredAndSortedData.filter(
          (o) => o.info.status === 1
        ).length;
        const offlineOutlets = filteredAndSortedData.filter(
          (o) => o.info.status === 0
        ).length;
        const expiredOutlets = filteredAndSortedData.filter(
          (o) => new Date(o.outletExp ? o.outletExp * 1000 : 0) < new Date()
        ).length;

        // Get unique groups and brands counts
        const uniqueGroups = [
          ...new Set(filteredAndSortedData.map((o) => o.group._id)),
        ].length;
        const uniqueBrands = [
          ...new Set(filteredAndSortedData.map((o) => o.brand.braKey)),
        ].length;

        // Create summary data
        const summaryData = [
          ["Total Outlets", totalOutlets.toString()],
          ["Online Outlets", onlineOutlets.toString()],
          ["Offline Outlets", offlineOutlets.toString()],
          ["Expired Outlets", expiredOutlets.toString()],
          ["Unique Groups", uniqueGroups.toString()],
          ["Unique Brands", uniqueBrands.toString()],
          ...(searchTerm ||
          filterStatus !== "all" ||
          filterGroup !== "all" ||
          filterBrand !== "all"
            ? [
                ["Filtered Results", "Yes"],
                ...(searchTerm ? [["Search Term", searchTerm]] : []),
                ...(filterStatus !== "all"
                  ? [
                      [
                        "Status Filter",
                        filterStatus.charAt(0).toUpperCase() +
                          filterStatus.slice(1),
                      ],
                    ]
                  : []),
                ...(filterGroup !== "all"
                  ? [
                      [
                        "Group Filter",
                        availableGroups.find((g) => g.id === filterGroup)
                          ?.name || "Unknown",
                      ],
                    ]
                  : []),
                ...(filterBrand !== "all"
                  ? [
                      [
                        "Brand Filter",
                        availableBrands.find((b) => b.id === filterBrand)
                          ?.name || "Unknown",
                      ],
                    ]
                  : []),
                ...(sortBy !== "status"
                  ? [
                      [
                        "Sorted By",
                        sortBy.charAt(0).toUpperCase() +
                          sortBy.slice(1) +
                          " (" +
                          sortOrder.toUpperCase() +
                          ")",
                      ],
                    ]
                  : []),
              ]
            : []),
        ];

        getData({
          type: "table",
          title: "Outlet Management Report",
          body: [
            // Main outlet data table
            {
              head: [
                [
                  "#",
                  "Status",
                  "Created At",
                  "Last Update",
                  "Group",
                  "Brand",
                  "Outlet",
                  // "Outlet Key",
                ],
              ],
              body: exportData,

              sheetName: "Outlets",
            },
            // Summary table
            {
              head: [["Summary", "Count"]],
              body: summaryData,
              sheetName: "Summary",
            },
          ],
          id: "exportPdf_OutletList",
        });
      }
      // eslint-disable-next-line
    }, [
      exportTime,
      load,
      filteredAndSortedData,
      searchTerm,
      filterStatus,
      filterGroup,
      filterBrand,
      sortBy,
      sortOrder,
      availableGroups,
      availableBrands,
      getData,
    ]);

    return (
      <div className="table-container">
        <table className="table table-striped defaultTable">
          <thead>
            <tr>
              <th
                scope="col"
                onClick={() => handleSort("status")}
                className="sortable"
              >
                Status
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("status")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("created")}
                className="sortable"
              >
                Created At
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("created")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("updated")}
                className="sortable"
              >
                Last Update
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("updated")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("group")}
                className="sortable"
              >
                Group
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("group")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("brand")}
                className="sortable"
              >
                Brand
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("brand")]}
                />
              </th>
              <th
                scope="col"
                onClick={() => handleSort("outlet")}
                className="sortable"
              >
                Outlet
                <FontAwesomeIcon
                  className="sort-icon"
                  icon={["fas", getSortIcon("outlet")]}
                />
              </th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.length > 0 ? (
              renderData()
            ) : (
              <tr>
                <td
                  colSpan={table.head.length}
                  className="text-center no-results"
                >
                  {searchTerm ||
                  filterStatus !== "all" ||
                  filterGroup !== "all" ||
                  filterBrand !== "all" ? (
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "search"]}
                        className="no-results-icon"
                      />
                      <p>No outlets found matching your search criteria</p>
                      <button className="btn btn-link" onClick={clearFilters}>
                        Clear filters to see all outlets
                      </button>
                    </div>
                  ) : (
                    <div>
                      <FontAwesomeIcon
                        icon={["fas", "store"]}
                        className="no-results-icon"
                      />
                      <p>No outlets available</p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const valueChange = async (groupData, group, brand, chain) => {
    let groupIndex = groupData.brands
      .map((brands, index) => ({
        ...brands,
        chains: brands.chains
          .map((chains, key) => ({ ...chains, index: key }))
          .find((chains) => chains.chaKey === chain.chaKey),
        index: index,
      }))
      .find((brands) => brands.braKey === chain.brand.braKey);

    const newStatus =
      groupData.brands[groupIndex.index].chains[groupIndex.chains.index]
        .status === 1
        ? 0
        : 1;

    // Update the data structure
    groupData.brands[groupIndex.index].chains[groupIndex.chains.index].status =
      newStatus;

    let dataToUpdate = {
      ...groupData,
    };

    try {
      await axios.put(
        props.chimeraserv.urlPost + "/group/update",
        dataToUpdate,
        {
          headers: {
            Authorization: props.user.data.usrKey,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      // Update the React state to trigger re-render
      setTable((prev) => ({
        ...prev,
        data: prev.data.map((item) =>
          item._id === groupData._id
            ? {
                ...item,
                brands: item.brands.map((b) =>
                  b.braKey === brand.braKey
                    ? {
                        ...b,
                        chains: b.chains.map((c) =>
                          c.chaKey === chain.chaKey
                            ? { ...c, status: newStatus }
                            : c
                        ),
                      }
                    : b
                ),
              }
            : item
        ),
      }));
    } catch (error) {
      console.error("Error updating outlet status:", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleGroupFilter = (e) => {
    setFilterGroup(e.target.value);
    setFilterBrand("all"); // Reset brand filter when group changes
  };

  const handleBrandFilter = (e) => {
    setFilterBrand(e.target.value);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterGroup("all");
    setFilterBrand("all");
    setSortBy("status");
    setSortOrder("desc");
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return "sort";
    return sortOrder === "asc" ? "sort-up" : "sort-down";
  };

  // Filter brands based on selected group
  const filteredBrands = useMemo(() => {
    if (filterGroup === "all") return availableBrands;
    return availableBrands.filter((brand) => brand.groupId === filterGroup);
  }, [availableBrands, filterGroup]);

  const renderData = () =>
    filteredAndSortedData.map((chain, key) => (
      <tr key={key}>
        <td>
          <div className="name">
            <div
              className={["status", chain.info.status ? "" : "offline"].join(
                " "
              )}
            />
            <span
              className={`status-badge ${
                chain.info.status ? "online" : "offline"
              }`}
            >
              {chain.info.status ? "Online" : "Offline"}
            </span>
          </div>
        </td>
        <td>
          <div className="date-info">
            {formatDate.long(chain.info.firstUpdate)}
          </div>
        </td>
        <td>
          <div className="date-info">
            {formatDate.long(chain.info.lastUpdate)}
          </div>
        </td>
        <td>
          <div className="group-info">
            <strong>{chain.group.name}</strong>
          </div>
        </td>
        <td>
          <div className="brand-info">
            <strong>{chain.brand.name}</strong>
          </div>
        </td>
        <td>
          <div className="date-info">
            <strong>
              {chain.name}{" "}
              {new Date(chain.outletExp ? chain.outletExp * 1000 : 0) <
                new Date() && <span className="expired-badge">Expired</span>}
            </strong>
          </div>
        </td>
        <td className="actions">
          {props.user.data.level === 0 ? (
            <div className="actions actionsAdmin">
              <label className="switch">
                <input
                  type="checkbox"
                  id={chain.chaKey}
                  checked={chain.status ? true : false}
                  onChange={() =>
                    valueChange(chain.data, chain.brand, chain.group, chain)
                  }
                />
                <span className="slider"></span>
              </label>
              <Link
                to={
                  location.pathname.split("/").splice(0, 4).join("/") +
                  "/edit/" +
                  chain.chaKey
                }
                state={{
                  outletData: chain,
                  brandData: chain.brand,
                  groupData: chain.group,
                }}
              >
                <FontAwesomeIcon className="icon" icon={["fas", "cog"]} /> Edit
              </Link>
            </div>
          ) : (
            <div />
          )}

          <Link
            to={
              location.pathname.split("/").splice(0, 4).join("/") +
              "/view/" +
              chain.chaKey
            }
            state={{
              outletData: chain,
              brandData: chain.brand,
              groupData: chain.group,
            }}
          >
            <FontAwesomeIcon className="icon" icon={["fas", "eye"]} /> View
          </Link>
        </td>
      </tr>
    ));

  const Loader =
    props.components && props.components.setting && props.components.setting[1]
      ? props.components.setting[1].component
      : () => <div>Loading...</div>;

  return (
    <div className="custForm" id="exportPdf_OutletList">
      <div className="viewTitle">List of Outlets</div>

      {/* Search and Filter Controls */}
      <div className="table-controls">
        <div className="search-section">
          <div className="search-input-group">
            <FontAwesomeIcon className="search-icon" icon={["fas", "search"]} />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search by outlet name, group, brand, or outlet key..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <FontAwesomeIcon icon={["fas", "times"]} />
              </button>
            )}
          </div>
        </div>

        <div className="filters-section">
          <select
            className="form-control filter-select"
            value={filterGroup}
            onChange={handleGroupFilter}
          >
            <option value="all">All Groups</option>
            {availableGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>

          <select
            className="form-control filter-select"
            value={filterBrand}
            onChange={handleBrandFilter}
          >
            <option value="all">All Brands</option>
            {filteredBrands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>

          <select
            className="form-control filter-select"
            value={filterStatus}
            onChange={handleStatusFilter}
          >
            <option value="all">All Status</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="expired">Expired</option>
          </select>

          <button
            type="button"
            className="btn btn-secondary clear-filters"
            onClick={clearFilters}
            title="Clear all filters"
          >
            <FontAwesomeIcon icon={["fas", "refresh"]} /> Clear
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {table.data && (
        <div className="results-summary">
          Showing {filteredAndSortedData.length} of {flatOutletData.length}{" "}
          outlets
          {(searchTerm ||
            filterStatus !== "all" ||
            filterGroup !== "all" ||
            filterBrand !== "all") && (
            <span className="filtered-indicator">
              <FontAwesomeIcon icon={["fas", "filter"]} /> Filtered
            </span>
          )}
        </div>
      )}

      <div>
        {!table.data ? (
          <Loader />
        ) : (
          <Window
            user={props.user}
            chimeraserv={props.chimeraserv}
            title={"Outlets"}
          >
            <OutletListContent />
          </Window>
        )}
      </div>
    </div>
  );
}

export default ListOutlet;
